import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';
import { 
  FileImage, Upload, Plus, Search, Filter, Eye, RefreshCw, 
  ChevronRight, AlertTriangle, CheckCircle, Clock, FileText,
  Calendar, Building, User, Layers, Download, ArrowUpRight,
  Sparkles, AlertCircle, X
} from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';

const DISCIPLINE_COLORS = {
  'Architectural': 'bg-blue-100 text-blue-700 border-blue-200',
  'Structural': 'bg-orange-100 text-orange-700 border-orange-200',
  'Civil': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Electrical': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Mechanical': 'bg-purple-100 text-purple-700 border-purple-200',
  'Wet Services': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Fire': 'bg-red-100 text-red-700 border-red-200',
  'Specialist': 'bg-pink-100 text-pink-700 border-pink-200',
  'Interior': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Landscaping': 'bg-green-100 text-green-700 border-green-200',
  'Façade': 'bg-slate-100 text-slate-700 border-slate-200',
  'General': 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_COLORS = {
  'Active': 'bg-emerald-100 text-emerald-700',
  'Superseded': 'bg-amber-100 text-amber-700',
  'Cancelled': 'bg-red-100 text-red-700',
  'On Hold': 'bg-slate-100 text-slate-700',
};

const DISTRIBUTION_COLORS = {
  'Not Distributed': 'bg-gray-100 text-gray-600',
  'Distributed': 'bg-blue-100 text-blue-700',
  'Acknowledged': 'bg-emerald-100 text-emerald-700',
};

export default function DrawingRegister({ projectId: propProjectId, initialFilter = '', project }) {
  const params = useParams();
  const projectId = propProjectId || params.projectId;
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions();
  
  const [drawings, setDrawings] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [consultants, setConsultants] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [issuePurposes, setIssuePurposes] = useState([]);
  
  // Get filter name for display
  const getFilterName = (filter) => {
    const names = {
      'recent': 'Recently Issued',
      'scope': 'Scope Changes',
      'pending': 'Pending Distribution',
      'all': null
    };
    return names[filter] || null;
  };
  
  // URL-based filter state
  const [urlFilter, setUrlFilter] = useState(initialFilter);
  
  // Clear filter handler
  const clearFilter = () => {
    setUrlFilter('');
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('filter');
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [latestOnly, setLatestOnly] = useState(true);
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    drawing_number: '',
    drawing_title: '',
    discipline: 'General',
    revision_code: '0',
    issue_purpose: 'Information',
    revision_notes: '',
    issued_by_consultant_id: '',
    issued_by_person: '',
    issue_date: '',
    received_date: new Date().toISOString().split('T')[0],
    sheet_count: 1,
    work_area_location: '',
    tags: []
  });

  useEffect(() => {
    loadData();
    loadReferenceData();
  }, [projectId, statusFilter, disciplineFilter, latestOnly]);

  const loadReferenceData = async () => {
    try {
      const [discRes, purposeRes, teamRes] = await Promise.all([
        api.drawings.getDisciplines(),
        api.drawings.getIssuePurposes(),
        api.teams.getProfessional(projectId).catch(() => ({ data: [] }))
      ]);
      setDisciplines(discRes.data || []);
      setIssuePurposes(purposeRes.data || []);
      setConsultants(teamRes.data || []);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        latest_only: latestOnly,
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        discipline: disciplineFilter !== 'all' ? disciplineFilter : undefined
      };
      const res = await api.drawings.getByProject(projectId, params);
      setDrawings(res.data.drawings || []);
      setStats(res.data.stats || {});
    } catch (error) {
      toast.error('Failed to load drawings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    setUploadFile(file);
    setExtractedData(null);
    
    // Auto-extract metadata
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.drawings.uploadPdf(projectId, formData, true);
      
      if (res.data.extraction) {
        const ext = res.data.extraction;
        setExtractedData(ext);
        
        // Pre-fill form with extracted data
        setUploadForm(prev => ({
          ...prev,
          drawing_number: ext.drawing_number || prev.drawing_number,
          drawing_title: ext.drawing_title || prev.drawing_title,
          discipline: ext.discipline || prev.discipline,
          revision_code: ext.revision_code || prev.revision_code,
          issue_purpose: ext.issue_purpose || prev.issue_purpose,
          issue_date: ext.issue_date || prev.issue_date,
          sheet_count: ext.sheet_count || prev.sheet_count,
          pdf_file_url: res.data.file_url,
          pdf_file_name: res.data.file_name,
          pdf_file_size: res.data.file_size
        }));
        
        // Find matching consultant
        if (ext.consultant_name) {
          const match = consultants.find(c => 
            c.company_name?.toLowerCase().includes(ext.consultant_name.toLowerCase())
          );
          if (match) {
            setUploadForm(prev => ({ ...prev, issued_by_consultant_id: match.id }));
          }
        }
        
        toast.success('Metadata extracted successfully');
      }
    } catch (error) {
      toast.error('Failed to extract metadata');
      console.error(error);
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateDrawing = async () => {
    if (!uploadForm.drawing_number) {
      toast.error('Drawing number is required');
      return;
    }
    if (!uploadForm.issued_by_consultant_id) {
      toast.error('Issuing consultant is required');
      return;
    }
    if (!uploadForm.pdf_file_url) {
      toast.error('Please upload a PDF file first');
      return;
    }
    
    setUploading(true);
    try {
      await api.drawings.create(projectId, uploadForm);
      toast.success(`Drawing ${uploadForm.drawing_number} created`);
      setShowUploadDialog(false);
      resetUploadForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create drawing');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setExtractedData(null);
    setUploadForm({
      drawing_number: '',
      drawing_title: '',
      discipline: 'General',
      revision_code: '0',
      issue_purpose: 'Information',
      revision_notes: '',
      issued_by_consultant_id: '',
      issued_by_person: '',
      issue_date: '',
      received_date: new Date().toISOString().split('T')[0],
      sheet_count: 1,
      work_area_location: '',
      tags: []
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  const filteredDrawings = drawings.filter(d => {
    // Search filter
    if (search) {
      const term = search.toLowerCase();
      const matchesSearch = (
        d.drawing_number?.toLowerCase().includes(term) ||
        d.drawing_title?.toLowerCase().includes(term) ||
        d.discipline?.toLowerCase().includes(term)
      );
      if (!matchesSearch) return false;
    }
    
    // URL-based filters
    if (urlFilter === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const issueDate = d.issue_date ? new Date(d.issue_date) : null;
      if (!issueDate || issueDate < sevenDaysAgo) return false;
    }
    
    if (urlFilter === 'scope') {
      if (!d.has_scope_change) return false;
    }
    
    if (urlFilter === 'pending') {
      if (d.distribution_status !== 'Not Distributed' && d.distribution_status !== 'Pending') return false;
    }
    
    return true;
  });

  return (
    <div className="space-y-4" data-testid="drawing-register">
      {/* Filter Banner */}
      {urlFilter && getFilterName(urlFilter) && (
        <div className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 text-teal-800 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">
              Filtered by: <span className="font-bold">{getFilterName(urlFilter)}</span>
            </span>
            <Badge variant="secondary" className="text-xs">{filteredDrawings.length} items</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilter} className="hover:bg-white/50">
            <X className="h-4 w-4 mr-1" /> Clear Filter
          </Button>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-2 sm:p-3">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Total</p>
          <p className="text-lg sm:text-2xl font-barlow font-bold">{stats.total_drawings || 0}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2 sm:p-3">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-600">Active</p>
          <p className="text-lg sm:text-2xl font-barlow font-bold text-emerald-700">{stats.active || 0}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 sm:p-3">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-600">Revisions</p>
          <p className="text-lg sm:text-2xl font-barlow font-bold text-amber-700">{stats.total_revisions || 0}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-2 sm:p-3">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-blue-600">Recent (7d)</p>
          <p className="text-lg sm:text-2xl font-barlow font-bold text-blue-700">{stats.recently_issued || 0}</p>
        </div>
        <div className={`border rounded-sm p-2 sm:p-3 ${stats.pending_acknowledgement > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
          <p className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider ${stats.pending_acknowledgement > 0 ? 'text-red-600' : 'text-slate-500'}`}>
            Pending
          </p>
          <p className={`text-lg sm:text-2xl font-barlow font-bold ${stats.pending_acknowledgement > 0 ? 'text-red-700' : 'text-slate-700'}`}>
            {stats.pending_acknowledgement || 0}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-2 sm:p-3">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Superseded</p>
          <p className="text-lg sm:text-2xl font-barlow font-bold text-slate-700">{stats.superseded || 0}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search drawings..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full sm:w-64 rounded-sm h-9"
              data-testid="drawing-search"
            />
          </div>
          <div className="flex gap-2">
            <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
              <SelectTrigger className="w-full sm:w-40 rounded-sm h-9">
                <SelectValue placeholder="All Disciplines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Disciplines</SelectItem>
                {disciplines.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-32 rounded-sm h-9">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Superseded">Superseded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <PrintScheduleButton moduleType="Drawing" data={drawings} project={project} />
          <Button
            onClick={() => setLatestOnly(!latestOnly)}
            variant={latestOnly ? "default" : "outline"}
            size="sm"
            className="rounded-sm"
          >
            <Layers className="h-4 w-4 mr-1" />
            {latestOnly ? 'Latest Only' : 'All Revisions'}
          </Button>
          <Button onClick={loadData} variant="outline" size="sm" className="rounded-sm">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {permissions.hasPermission('can_upload_drawings') && (
            <Dialog open={showUploadDialog} onOpenChange={(open) => {
              setShowUploadDialog(open);
              if (!open) resetUploadForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-sm bg-slate-900 text-white" data-testid="add-drawing-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Drawing
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-barlow uppercase">Upload Drawing</DialogTitle>
              </DialogHeader>
              
              {/* File Upload */}
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-sm p-6 text-center">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    id="drawing-upload"
                  />
                  <label htmlFor="drawing-upload" className="cursor-pointer">
                    {extracting ? (
                      <div className="flex flex-col items-center">
                        <Sparkles className="h-10 w-10 text-blue-500 animate-pulse mb-2" />
                        <p className="text-sm text-slate-600">Extracting metadata with AI...</p>
                      </div>
                    ) : uploadFile ? (
                      <div className="flex flex-col items-center">
                        <FileText className="h-10 w-10 text-emerald-500 mb-2" />
                        <p className="text-sm font-medium text-slate-700">{uploadFile.name}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {extractedData?.extraction_confidence 
                            ? `AI Confidence: ${extractedData.extraction_confidence}%`
                            : 'Click to replace'}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-10 w-10 text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">Click to upload PDF drawing</p>
                        <p className="text-xs text-slate-400 mt-1">AI will extract metadata automatically</p>
                      </div>
                    )}
                  </label>
                </div>

                {/* Extraction Notes */}
                {extractedData?.extraction_notes?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                    <div className="flex items-center gap-2 text-amber-700 text-sm mb-1">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-bold">AI Notes</span>
                    </div>
                    <ul className="text-xs text-amber-600 list-disc list-inside">
                      {extractedData.extraction_notes.map((note, i) => (
                        <li key={i}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Form Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase">Drawing Number *</Label>
                    <Input
                      value={uploadForm.drawing_number}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, drawing_number: e.target.value }))}
                      placeholder="e.g., A-101"
                      className="rounded-sm mt-1"
                      data-testid="drawing-number-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Revision Code *</Label>
                    <Input
                      value={uploadForm.revision_code}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, revision_code: e.target.value }))}
                      placeholder="e.g., 0, A, P01"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-bold uppercase">Drawing Title</Label>
                    <Input
                      value={uploadForm.drawing_title}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, drawing_title: e.target.value }))}
                      placeholder="e.g., Ground Floor Plan"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Discipline</Label>
                    <Select 
                      value={uploadForm.discipline} 
                      onValueChange={(v) => setUploadForm(prev => ({ ...prev, discipline: v }))}
                    >
                      <SelectTrigger className="rounded-sm mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {disciplines.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Issue Purpose</Label>
                    <Select 
                      value={uploadForm.issue_purpose} 
                      onValueChange={(v) => setUploadForm(prev => ({ ...prev, issue_purpose: v }))}
                    >
                      <SelectTrigger className="rounded-sm mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {issuePurposes.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Issued By Consultant *</Label>
                    <Select 
                      value={uploadForm.issued_by_consultant_id} 
                      onValueChange={(v) => {
                        // Find the selected consultant and auto-populate person name
                        const selectedConsultant = consultants.find(c => c.id === v);
                        setUploadForm(prev => ({ 
                          ...prev, 
                          issued_by_consultant_id: v,
                          // Auto-populate person name from consultant's contact
                          issued_by_person: selectedConsultant?.contact_name || 
                            (selectedConsultant?.primary_contact?.name && selectedConsultant?.primary_contact?.surname 
                              ? `${selectedConsultant.primary_contact.name} ${selectedConsultant.primary_contact.surname}`
                              : prev.issued_by_person)
                        }));
                      }}
                    >
                      <SelectTrigger className="rounded-sm mt-1">
                        <SelectValue placeholder="Select consultant" />
                      </SelectTrigger>
                      <SelectContent>
                        {consultants.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{c.company_name}</span>
                              {c.discipline && (
                                <span className="text-xs text-slate-500">({c.discipline})</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Issued By Person</Label>
                    <Input
                      value={uploadForm.issued_by_person}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, issued_by_person: e.target.value }))}
                      placeholder="Person name"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Issue Date</Label>
                    <Input
                      type="date"
                      value={uploadForm.issue_date}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, issue_date: e.target.value }))}
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Received Date</Label>
                    <Input
                      type="date"
                      value={uploadForm.received_date}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, received_date: e.target.value }))}
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Sheet Count</Label>
                    <Input
                      type="number"
                      min="1"
                      value={uploadForm.sheet_count}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, sheet_count: parseInt(e.target.value) || 1 }))}
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-bold uppercase">Revision Notes</Label>
                    <Textarea
                      value={uploadForm.revision_notes}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, revision_notes: e.target.value }))}
                      placeholder="Describe the changes made in this revision..."
                      rows={3}
                      className="rounded-sm mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Optionally describe changes for the first revision</p>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateDrawing} 
                  disabled={uploading}
                  className="rounded-sm bg-slate-900"
                  data-testid="create-drawing-submit"
                >
                  {uploading ? 'Creating...' : 'Create Drawing'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Drawing Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]" data-testid="drawing-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Drawing No.</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Title</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Discipline</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600 hidden sm:table-cell">Rev</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600 hidden md:table-cell">Issued By</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600 hidden md:table-cell">Issue Date</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Status</th>
              <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600 hidden lg:table-cell">Links</th>
              <th className="text-center p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={9} className="p-8 text-center">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-500 text-sm">Loading drawings...</p>
                </td>
              </tr>
            ) : filteredDrawings.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-12 text-center">
                  <FileImage className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No drawings found</p>
                  <p className="text-slate-400 text-xs mt-1">Upload your first drawing to get started</p>
                </td>
              </tr>
            ) : (
              filteredDrawings.map((drawing) => {
                const rev = drawing.current_revision || {};
                return (
                  <tr 
                    key={drawing.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/projects/${projectId}/drawings/${drawing.id}`)}
                    data-testid={`drawing-row-${drawing.id}`}
                  >
                    <td className="p-3">
                      <span className="font-mono font-bold text-slate-900">{drawing.drawing_number}</span>
                    </td>
                    <td className="p-3 text-slate-600 max-w-[150px] sm:max-w-xs truncate">
                      {drawing.drawing_title || 'Untitled'}
                    </td>
                    <td className="p-3">
                      <Badge className={`${DISCIPLINE_COLORS[drawing.discipline] || DISCIPLINE_COLORS.General} rounded-sm text-xs`}>
                        {drawing.discipline}
                      </Badge>
                    </td>
                    <td className="p-3 hidden sm:table-cell">
                      <span className="font-mono text-slate-700">Rev {drawing.current_revision_code || '0'}</span>
                      {drawing.revision_count > 1 && (
                        <span className="text-xs text-slate-400 ml-1">({drawing.revision_count})</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600 text-xs hidden md:table-cell">
                      {rev.issued_by_consultant_name || drawing.originating_consultant_name || 'N/A'}
                    </td>
                    <td className="p-3 text-slate-500 text-xs hidden md:table-cell">
                      {formatDate(rev.issue_date)}
                      {rev.days_since_issued && (
                        <span className="text-slate-400 ml-1">({rev.days_since_issued}d)</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge className={`${STATUS_COLORS[drawing.status] || STATUS_COLORS.Active} rounded-sm text-xs`}>
                        {drawing.status}
                      </Badge>
                      {rev.acknowledgement_overdue && (
                        <Badge className="bg-red-100 text-red-700 rounded-sm text-xs ml-1">
                          <AlertTriangle className="h-3 w-3 mr-0.5" />
                          Overdue
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 hidden lg:table-cell">
                      <div className="flex gap-1">
                        {drawing.linked_rfi_count > 0 && (
                          <Badge variant="outline" className="text-xs rounded-sm">
                            {drawing.linked_rfi_count} RFI
                          </Badge>
                        )}
                        {drawing.linked_delay_event_count > 0 && (
                          <Badge variant="outline" className="text-xs rounded-sm text-orange-600 border-orange-300">
                            {drawing.linked_delay_event_count} Event
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/drawings/${drawing.id}`); }}
                      >
                        <Eye className="h-4 w-4 text-slate-400" />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
